import { CONTAINER_WIDTH_IN_MMM, LASHING_BRIDGE_END_CLEARANCE_IN_MM, LASHING_BRIDGE_MIN_DOUBLE_GAP_IN_MM, LASHING_BRIDGE_RAIL_HEIGHT_TIERS, LASHING_BRIDGE_THICKNESS_IN_MM, LASHING_BRIDGE_TOP_WALKWAY_DEPTH_IN_MM, } from "../consts";
import { LASHING_BRIDGE_SIDES, getBayLevelRows, lashingBridgeHeightInMm, resolveLashingBridgeRows, } from "./lashingBridgesHelpers";
import { getBayEnds, getLashingBridgeSlots, getLowLongContainerEnds, getTypicalLashingBridgeGap, } from "./getLashingBridgeSlots";
import { pad2, pad3 } from "@tedivo/tedivo-pure-helpers";
import { BayLevelEnum } from "open-vessel-definition";
/**
 * Geometry (in mm, OVD coordinates) of every lashing bridge of the vessel, to be drawn by the
 * side view and the 3D view. Consumers apply their own offsets (like the bottom base offsets).
 *
 * - LCG: the bridge's `lcg`, or {@link getDefaultLashingBridgeLcg} (the middle of the gap with
 *   the next block of bays).
 * - Height of each row: its `perRow` override or the bridge defaults. `height` wins over `tiers`.
 * - Rows come from {@link getBayLevelRows}. `perRow` keys that the bay doesn't have are ignored.
 * - Frame of each row (structure top and walkways): {@link getLashingBridgeFrameLevels}.
 */
export function getLashingBridgesGeometry(p, options = {}) {
    var _a, _b, _c, _d;
    const tierHeightInMm = (_a = options.tierHeightInMm) !== null && _a !== void 0 ? _a : p.averageContainerHeightInMm;
    const thickness = (_b = options.thicknessInMm) !== null && _b !== void 0 ? _b : LASHING_BRIDGE_THICKNESS_IN_MM;
    const topWalkwayDepth = Math.min((_c = options.topWalkwayDepthInMm) !== null && _c !== void 0 ? _c : LASHING_BRIDGE_TOP_WALKWAY_DEPTH_IN_MM, thickness);
    const railHeightTiers = (_d = options.railHeightTiers) !== null && _d !== void 0 ? _d : LASHING_BRIDGE_RAIL_HEIGHT_TIERS;
    const result = [];
    p.bayLevelPositionsAbove.forEach((blp) => {
        const info = blp.lashingBridges;
        if (!info)
            return;
        const rows = getBayLevelRows(p.sizeSummary, blp.bayData);
        if (!rows.length)
            return;
        const tcgs = rows.map((row) => getRowTcg(p, blp, row));
        LASHING_BRIDGE_SIDES.forEach((side) => {
            const bridge = info[side];
            if (!bridge)
                return;
            const rowsGeometry = [];
            resolveLashingBridgeRows(bridge, rows).forEach((resolved, idx) => {
                const heightAboveBottom = lashingBridgeHeightInMm(resolved, tierHeightInMm);
                const tcg = tcgs[idx];
                if (heightAboveBottom <= 0 || tcg === undefined)
                    return;
                const stackBottomBase = getRowStackBottomBase(p, blp, resolved.row);
                if (stackBottomBase === undefined)
                    return;
                const top = stackBottomBase + heightAboveBottom;
                rowsGeometry.push(Object.assign({ row: resolved.row, tcg, halfWidth: getHalfWidth(tcgs, idx), stackBottomBase,
                    heightAboveBottom,
                    top }, getLashingBridgeFrameLevels(stackBottomBase, top, tierHeightInMm, railHeightTiers)));
            });
            if (!rowsGeometry.length)
                return;
            const lcgIsDefault = bridge.lcg === undefined;
            const lcg = lcgIsDefault
                ? getDefaultLashingBridgeLcg(p, blp.isoBay, side, {
                    thicknessInMm: thickness,
                    tierHeightInMm,
                    structureHeightInMm: Math.max(...rowsGeometry.map((r) => r.structureTop - r.stackBottomBase)),
                })
                : bridge.lcg;
            if (lcg === undefined || !Number.isFinite(lcg))
                return;
            const lcgCenter = lcg;
            result.push({
                isoBay: blp.isoBay,
                side,
                lcg,
                lcgIsDefault,
                lcgCenter,
                thickness,
                topWalkwayDepth,
                tierHeight: tierHeightInMm,
                rows: rowsGeometry,
                minBottomBase: Math.min(...rowsGeometry.map((r) => r.stackBottomBase)),
                maxTop: Math.max(...rowsGeometry.map((r) => r.top)),
                maxStructureTop: Math.max(...rowsGeometry.map((r) => r.structureTop)),
            });
        });
    });
    return result;
}
/**
 * LCG of a lashing bridge whose OVD doesn't define one, from its slot ({@link getLashingBridgeSlots}):
 * - in the middle of the gap with the neighbour block of bays, when there is no vessel part in
 *   between, so it stands between both stacks;
 * - otherwise (the ends of the ship, a vessel part like a deckhouse follows, or the blocks touch)
 *   it stands off the end of the block by half the typical gap between blocks, where it would be
 *   if another block followed. Without such gaps, by half its thickness plus a clearance.
 *
 * A bridge stored on a position that isn't a slot (old data) uses the neighbour bay: centered when
 * only one bridge fits in between, standing off the end of the bay otherwise.
 *
 * The ends come from the 20' and 40' slots: longer containers overhang the bay ends, but they
 * are usually stowed on top, above the bridges. When a bay stows them lower than the top of the
 * bridge structure (handrail included), the ends of those containers are used instead
 * ({@link getLowLongContainerEnds}), so the bridge clears them.
 */
export function getDefaultLashingBridgeLcg(p, isoBay, side, { thicknessInMm = LASHING_BRIDGE_THICKNESS_IN_MM, tierHeightInMm = p.averageContainerHeightInMm, structureHeightInMm, } = {}) {
    const height = structureHeightInMm !== null && structureHeightInMm !== void 0 ? structureHeightInMm : getLashingBridgeStructureHeight(p, isoBay, side, tierHeightInMm);
    /** `end` of `bays` on `endSide`, pushed out by the longer containers stowed below the bridge top */
    const clearEnd = (end, bays, endSide) => {
        const long = height > 0 ? getLowLongContainerEnds(p, bays, height, tierHeightInMm) : undefined;
        if (!long)
            return end;
        return endSide === "fore" ? Math.max(end, long.fore) : Math.min(end, long.aft);
    };
    const otherSide = side === "fore" ? "aft" : "fore";
    const slots = getLashingBridgeSlots(p);
    const sign = side === "fore" ? 1 : -1;
    const typicalGap = getTypicalLashingBridgeGap(slots);
    const standOff = typicalGap !== undefined
        ? typicalGap * 0.5
        : thicknessInMm * 0.5 + LASHING_BRIDGE_END_CLEARANCE_IN_MM;
    const slot = slots.find((s) => s.isoBay === isoBay && s.side === side);
    if (slot) {
        if (slot.end === undefined)
            return undefined;
        const end = clearEnd(slot.end, splitBlock(slot.block), side);
        const centered = !slot.partBetween &&
            slot.gap !== undefined &&
            slot.gap > 0 &&
            slot.neighbourEnd !== undefined &&
            slot.neighbourBlock !== undefined;
        if (!centered)
            return end + sign * standOff;
        const neighbourEnd = clearEnd(slot.neighbourEnd, splitBlock(slot.neighbourBlock), otherSide);
        return (end + neighbourEnd) * 0.5;
    }
    // Not an offered position: use the neighbour bay
    const ends = getBayEnds(p, isoBay);
    if (!ends)
        return undefined;
    const end = clearEnd(side === "fore" ? ends.fore : ends.aft, [isoBay], side);
    const nextBay = pad3(Number(isoBay) - sign * 2);
    const next = Number(nextBay) >= 1 ? getBayEnds(p, nextBay) : undefined;
    const nextEnd = next
        ? clearEnd(side === "fore" ? next.aft : next.fore, [nextBay], otherSide)
        : undefined;
    const gap = nextEnd !== undefined ? sign * (nextEnd - end) : undefined;
    return gap !== undefined && gap > 0 && gap < LASHING_BRIDGE_MIN_DOUBLE_GAP_IN_MM
        ? (end + nextEnd) * 0.5
        : end + sign * standOff;
}
/**
 * Height of the structure (handrail included) of the bridge stored on a bay and side, over the
 * stack bottom, in mm: the highest of its rows. 0 when there is no such bridge.
 */
export function getLashingBridgeStructureHeight(p, isoBay, side, tierHeightInMm, railHeightTiers = LASHING_BRIDGE_RAIL_HEIGHT_TIERS) {
    var _a;
    const blp = p.bayLevelPositionsDict[`${isoBay}-${BayLevelEnum.ABOVE}`];
    const bridge = (_a = blp === null || blp === void 0 ? void 0 : blp.lashingBridges) === null || _a === void 0 ? void 0 : _a[side];
    if (!bridge)
        return 0;
    const heights = resolveLashingBridgeRows(bridge, getBayLevelRows(p.sizeSummary, blp.bayData))
        .map((r) => lashingBridgeHeightInMm(r, tierHeightInMm))
        .filter((h) => h > 0);
    return heights.length ? Math.max(...heights) + tierHeightInMm * railHeightTiers : 0;
}
/** Bays of a block label (`"005-007"` or `"009"`) */
function splitBlock(block) {
    return block.split("-");
}
/**
 * Frame of a lashing bridge row, as drawn by the side view and the 3D view:
 * - the top walkway is at the bridge top (`tiers`/`height`);
 * - the handrail rises `railHeightTiers` over it: that is the top of the structure
 *   (a 3-tier bridge tops out at 3.5 tiers);
 * - there is a walkway at every tier level below the top one, skipping one closer than a quarter tier.
 */
export function getLashingBridgeFrameLevels(bottom, top, tierHeight, railHeightTiers) {
    const walkways = [];
    if (tierHeight > 0)
        for (let y = bottom + tierHeight; y < top - tierHeight * 0.25; y += tierHeight)
            walkways.push(y);
    walkways.push(top);
    return { structureTop: top + tierHeight * railHeightTiers, walkways };
}
function getRowTcg(p, blp, row) {
    var _a, _b;
    const tcg = (_b = (_a = blp.perRowAverageBayTcg.get(row)) !== null && _a !== void 0 ? _a : blp.tcgByRow.get(row)) !== null && _b !== void 0 ? _b : p.getMissingTcg(row, blp.isoBay, BayLevelEnum.ABOVE);
    return tcg !== undefined && Number.isFinite(tcg) ? tcg : undefined;
}
function getRowStackBottomBase(p, blp, row) {
    const rowBottom = blp.rowBottoms.get(row);
    const minTier = Number.isFinite(blp.minTier) ? blp.minTier : p.sizeSummary.minAboveTier;
    const candidates = [
        rowBottom === null || rowBottom === void 0 ? void 0 : rowBottom.bottomBase,
        (rowBottom === null || rowBottom === void 0 ? void 0 : rowBottom.bottomTier) !== undefined ? p.getMissingBottomBase(rowBottom.bottomTier) : undefined,
        minTier !== undefined ? p.getMissingBottomBase(pad2(minTier)) : undefined,
        p.aboveBottomBases[blp.isoBay],
    ];
    return candidates.find((v) => v !== undefined && Number.isFinite(v));
}
/** Half the distance to the nearest neighbour row, so the bridge is continuous across the rows */
function getHalfWidth(tcgs, idx) {
    const tcg = tcgs[idx];
    if (tcg === undefined)
        return CONTAINER_WIDTH_IN_MMM * 0.5;
    const distances = [tcgs[idx - 1], tcgs[idx + 1]]
        .filter((t) => t !== undefined)
        .map((t) => Math.abs(t - tcg))
        .filter((d) => d > 0);
    return distances.length ? Math.min(...distances) * 0.5 : CONTAINER_WIDTH_IN_MMM * 0.5;
}
